import Vue from 'vue';
import Vuex from 'vuex';
import {
  getAuthErrorMessage,
  signInWithGoogle,
  signOutUser,
  watchAuthState,
} from '@/services/auth';
import { fetchProtectedDraftContent, fetchPublishedContent } from '@/services/draftContent';
import { clearDraftMediaAssets } from '@/services/mediaAssets';
import {
  isAuthorizedReckonUser,
  readDraftModePreference,
  writeDraftModePreference,
} from '@/lib/draftMode';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: null,
    authReady: false,
    authError: '',
    draftModeEnabled: false,
    draftContent: { narratives: [], venues: [] },
    publishedRemoteContent: { narratives: [], venues: [] },
    draftContentError: '',
  },
  getters: {
    currentUser: (state) => state.currentUser,
    isAuthenticated: (state) => Boolean(state.currentUser),
    authReady: (state) => state.authReady,
    authError: (state) => state.authError,
    isAuthorizedReckonUser: (state) => isAuthorizedReckonUser(state.currentUser),
    draftModeEnabled: (state, getters) => getters.isAuthorizedReckonUser
      && state.draftModeEnabled,
    draftContent: (state, getters) => (getters.draftModeEnabled
      ? state.draftContent
      : { narratives: [], venues: [] }),
    draftContentError: (state) => state.draftContentError,
    visibleRemoteContent: (state, getters) => ({
      narratives: [...state.publishedRemoteContent.narratives, ...(getters.draftModeEnabled ? state.draftContent.narratives : [])],
      venues: [...state.publishedRemoteContent.venues, ...(getters.draftModeEnabled ? state.draftContent.venues : [])],
    }),
  },
  mutations: {
    setCurrentUser(state, user) {
      state.currentUser = user;
    },
    setAuthReady(state, isReady) {
      state.authReady = isReady;
    },
    setAuthError(state, message) {
      state.authError = message;
    },
    setDraftModeEnabled(state, enabled) {
      state.draftModeEnabled = enabled;
    },
    setDraftContent(state, content) {
      state.draftContent = content;
    },
    setDraftContentError(state, message) {
      state.draftContentError = message;
    },
    setPublishedRemoteContent(state, content) {
      state.publishedRemoteContent = content;
    },
  },
  actions: {
    initializeAuth({ commit, dispatch }) {
      return watchAuthState(
        (user) => {
          commit('setCurrentUser', user);
          commit('setAuthReady', true);
          dispatch('loadPublishedContent');
          dispatch('syncDraftAuthorization');
        },
        (error) => {
          commit('setAuthError', getAuthErrorMessage(error));
          commit('setAuthReady', true);
        },
      );
    },
    async loadPublishedContent({ commit }) {
      try {
        commit('setPublishedRemoteContent', await fetchPublishedContent());
      } catch (error) {
        commit('setPublishedRemoteContent', { narratives: [], venues: [] });
      }
    },
    async syncDraftAuthorization({ commit, getters, dispatch }) {
      if (!getters.isAuthorizedReckonUser) {
        commit('setDraftModeEnabled', false);
        commit('setDraftContent', { narratives: [], venues: [] });
        clearDraftMediaAssets();
        writeDraftModePreference(window.localStorage, false);
        return;
      }

      const enabled = readDraftModePreference(window.localStorage);
      commit('setDraftModeEnabled', enabled);
      if (enabled) await dispatch('loadDraftContent');
    },
    async setDraftMode({ commit, getters, dispatch }, enabled) {
      const nextEnabled = Boolean(enabled && getters.isAuthorizedReckonUser);
      commit('setDraftModeEnabled', nextEnabled);
      writeDraftModePreference(window.localStorage, nextEnabled);
      if (!nextEnabled) {
        commit('setDraftContent', { narratives: [], venues: [] });
        clearDraftMediaAssets();
        commit('setDraftContentError', '');
        return;
      }
      await dispatch('loadDraftContent');
    },
    async loadDraftContent({ commit, getters }) {
      if (!getters.isAuthorizedReckonUser || !getters.draftModeEnabled) return;
      commit('setDraftContentError', '');
      try {
        commit('setDraftContent', await fetchProtectedDraftContent());
      } catch (error) {
        commit('setDraftModeEnabled', false);
        commit('setDraftContent', { narratives: [], venues: [] });
        writeDraftModePreference(window.localStorage, false);
        commit('setDraftContentError', 'Draft content could not be loaded. Your access may have changed.');
      }
    },
    async signInWithGoogle({ commit }) {
      commit('setAuthError', '');

      try {
        await signInWithGoogle();
      } catch (error) {
        commit('setAuthError', getAuthErrorMessage(error));
        throw error;
      }
    },
    async signOut({ commit }) {
      commit('setAuthError', '');
      commit('setDraftModeEnabled', false);
      commit('setDraftContent', { narratives: [], venues: [] });
      clearDraftMediaAssets();
      writeDraftModePreference(window.localStorage, false);

      try {
        await signOutUser();
      } catch (error) {
        commit('setAuthError', 'Sign out did not complete. Please try again.');
        throw error;
      }
    },
  },
  modules: {
  },
});
